/* ==========================================================================
   BLVNKS — Reusable Artwork Editor
   Requires Fabric.js to be loaded before this file.

   This module owns only visual artwork editing:
   - garment image rendering
   - printable-area guide
   - drag / resize / rotate
   - physical-width conversion
   - SVG preview normalization helper

   Pricing, Shopify cart logic, production quantities, review routing, and
   order submission stay outside this file.
   ========================================================================== */

(function (global) {
  'use strict';

  function finiteNumber(value, fallback) {
    var n = Number(value);
    return isFinite(n) ? n : fallback;
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function round2(value) {
    return Math.round(Number(value || 0) * 100) / 100;
  }

  function normalizeSignedAngle(angle) {
    angle = Number(angle) || 0;
    return ((angle + 180) % 360 + 360) % 360 - 180;
  }

  function loadFabricImage(url, callback) {
    if (!url || !global.fabric) {
      callback(null);
      return;
    }

    var opts = /^https?:/i.test(String(url))
      ? { crossOrigin: 'anonymous' }
      : {};

    global.fabric.Image.fromURL(
      url,
      function (img) {
        callback(img || null);
      },
      opts
    );
  }

  function normalizeSvgPreview(file, callback) {
    if (!file || !global.DOMParser || !global.XMLSerializer) {
      callback(null, null);
      return;
    }

    var reader = new FileReader();

    reader.onload = function () {
      var host = null;

      try {
        var parser = new DOMParser();
        var doc = parser.parseFromString(
          String(reader.result || ''),
          'image/svg+xml'
        );
        var svg = doc.documentElement;

        if (
          !svg ||
          String(svg.nodeName).toLowerCase() !== 'svg' ||
          doc.querySelector('parsererror')
        ) {
          throw new Error('Invalid SVG document');
        }

        if (!svg.getAttribute('xmlns')) {
          svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        }

        host = document.createElement('div');
        host.style.position = 'fixed';
        host.style.left = '-10000px';
        host.style.top = '-10000px';
        host.style.width = '1000px';
        host.style.height = '1000px';
        host.style.visibility = 'hidden';
        host.style.pointerEvents = 'none';
        host.innerHTML = new XMLSerializer().serializeToString(svg);
        document.body.appendChild(host);

        var liveSvg = host.querySelector('svg');

        if (!liveSvg || typeof liveSvg.getBBox !== 'function') {
          throw new Error('SVG bounds unavailable');
        }

        liveSvg.style.overflow = 'visible';

        var bbox = liveSvg.getBBox();
        var bw = Number(bbox.width);
        var bh = Number(bbox.height);

        if (!(bw > 0) || !(bh > 0)) {
          throw new Error('SVG has no measurable artwork bounds');
        }

        var pad = Math.max(bw, bh) * 0.01;
        var vx = Number(bbox.x) - pad;
        var vy = Number(bbox.y) - pad;
        var vw = bw + pad * 2;
        var vh = bh + pad * 2;

        liveSvg.setAttribute('viewBox', [vx, vy, vw, vh].join(' '));
        liveSvg.setAttribute('width', vw);
        liveSvg.setAttribute('height', vh);
        liveSvg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
        liveSvg.style.overflow = 'hidden';

        var markup = new XMLSerializer().serializeToString(liveSvg);
        var blob = new Blob(
          [markup],
          { type: 'image/svg+xml;charset=utf-8' }
        );
        var url = URL.createObjectURL(blob);

        callback(url, vw / vh);
      } catch (err) {
        console.warn(
          '[blvnks-editor] SVG preview normalization failed',
          file.name,
          err
        );
        callback(null, null);
      } finally {
        if (host && host.parentNode) {
          host.parentNode.removeChild(host);
        }
      }
    };

    reader.onerror = function () {
      callback(null, null);
    };

    reader.readAsText(file);
  }

  function BlvnksArtworkEditor(options) {
    if (!(this instanceof BlvnksArtworkEditor)) {
      return new BlvnksArtworkEditor(options);
    }

    this.options = options || {};
    this.canvas = null;
    this.artworkObject = null;
    this.garmentObject = null;
    this.printGuideObjects = [];
    this.destroyed = false;
    this.artworkAspect = null;
    this._artworkLoadId = 0;

    this.logicalWidth = finiteNumber(
      this.options.logicalWidth,
      100
    );
    this.logicalHeight = finiteNumber(
      this.options.logicalHeight,
      110
    );

    this.unitsPerInch = Math.max(
      0.0001,
      finiteNumber(this.options.unitsPerInch, 1)
    );

    this.minWidthIn = Math.max(
      0.1,
      finiteNumber(this.options.minWidthIn, 1)
    );

    this.maxWidthIn = Math.max(
      this.minWidthIn,
      finiteNumber(this.options.maxWidthIn, 99)
    );

    this.minAngle = finiteNumber(this.options.minAngle, -45);
    this.maxAngle = finiteNumber(this.options.maxAngle, 45);

    this.state = {
      cx: finiteNumber(this.options.state && this.options.state.cx, 50),
      cy: finiteNumber(this.options.state && this.options.state.cy, 50),
      angle: clamp(
        normalizeSignedAngle(
          this.options.state && this.options.state.angle
        ),
        this.minAngle,
        this.maxAngle
      ),
      widthIn: clamp(
        finiteNumber(
          this.options.state && this.options.state.widthIn,
          4
        ),
        this.minWidthIn,
        this.maxWidthIn
      )
    };

    this.mount();
  }

  BlvnksArtworkEditor.prototype.mount = function () {
    var self = this;

    if (!global.fabric) {
      self.emitError(new Error('Fabric.js is not loaded'));
      return;
    }

    var canvasEl = self.options.canvas;

    if (!canvasEl) {
      self.emitError(new Error('Editor canvas element is missing'));
      return;
    }

    var container =
      self.options.container ||
      canvasEl.parentElement ||
      canvasEl;

    var width = finiteNumber(
      self.options.width,
      Math.floor(container.clientWidth || 440)
    );

    width = Math.min(
      finiteNumber(self.options.maxCanvasWidth, 440),
      Math.max(
        finiteNumber(self.options.minCanvasWidth, 260),
        width
      )
    );

    var height = Math.round(
      width * (self.logicalHeight / self.logicalWidth)
    );

    self.scaleX = width / self.logicalWidth;
    self.scaleY = height / self.logicalHeight;

    self.canvas = new global.fabric.Canvas(canvasEl, {
      width: width,
      height: height,
      selection: false,
      preserveObjectStacking: true,
      uniformScaling: true,
      backgroundColor: '#ffffff'
    });

    self.printBounds = self.buildPrintBounds(
      self.options.printArea
    );

    loadFabricImage(
      self.options.garmentUrl,
      function (garment) {
        if (self.destroyed) return;

        if (garment) {
          self.garmentObject = garment;

          var garmentScale = Math.min(
            width / Math.max(garment.width || 1, 1),
            height / Math.max(garment.height || 1, 1)
          );

          garment.set({
            left: width / 2,
            top: height / 2,
            originX: 'center',
            originY: 'center',
            scaleX: garmentScale,
            scaleY: garmentScale,
            selectable: false,
            evented: false,
            objectCaching: false,
            excludeFromExport: true,
            blvGarmentObject: true
          });

          self.garmentUrl = self.options.garmentUrl;
          self.canvas.add(garment);
          garment.sendToBack();
        }

        self.addPrintGuide();
        self.loadArtwork();
        self.canvas.requestRenderAll();
      }
    );
  };

  BlvnksArtworkEditor.prototype.resize = function () {
    var self = this;

    if (!self.canvas || self.destroyed) return;

    var container = self.options.container;
    var width = Math.floor(
      (container && container.clientWidth) || self.canvas.getWidth()
    );

    width = Math.min(
      finiteNumber(self.options.maxCanvasWidth, 720),
      Math.max(finiteNumber(self.options.minCanvasWidth, 220), width)
    );

    var height = Math.round(
      width * (self.logicalHeight / self.logicalWidth)
    );
    var oldWidth = self.canvas.getWidth();

    if (!oldWidth || Math.abs(oldWidth - width) < 2) {
      self.canvas.requestRenderAll();
      return;
    }

    var factor = width / oldWidth;

    self.scaleX = width / self.logicalWidth;
    self.scaleY = height / self.logicalHeight;

    self.canvas.getObjects().forEach(function (obj) {
      if (obj.blvPrintGuide) return;
      obj.scaleX = (Number(obj.scaleX) || 1) * factor;
      obj.scaleY = (Number(obj.scaleY) || 1) * factor;
      obj.left = (Number(obj.left) || 0) * factor;
      obj.top = (Number(obj.top) || 0) * factor;
      obj.setCoords();
    });

    self.canvas.setDimensions({ width: width, height: height });
    self.printBounds = self.buildPrintBounds(self.options.printArea);
    self.refreshPrintGuide();
  };

  BlvnksArtworkEditor.prototype.buildPrintBounds = function (area) {
    area = area || {
      x: 24,
      y: 22,
      w: 52,
      h: 70
    };

    return {
      left: finiteNumber(area.x, 24) * this.scaleX,
      top: finiteNumber(area.y, 22) * this.scaleY,
      width: finiteNumber(area.w, 52) * this.scaleX,
      height: finiteNumber(area.h, 70) * this.scaleY
    };
  };

  function formatAreaInches(value) {
    var n = Math.round(Number(value) * 10) / 10;
    if (!isFinite(n) || n <= 0) return '';
    if (Math.abs(n - Math.round(n)) < 0.05) return String(Math.round(n));
    return n.toFixed(1);
  }

  BlvnksArtworkEditor.prototype.printAreaLabel = function () {
    var area = this.options.printArea || {};
    var units = this.unitsPerInch || 1;
    var w = formatAreaInches(finiteNumber(area.w, 0) / units);
    var h = formatAreaInches(finiteNumber(area.h, 0) / units);
    if (!w || !h) return '';
    return w + ' \u00d7 ' + h + ' in';
  };

  BlvnksArtworkEditor.prototype.addPrintGuide = function () {
    var self = this;

    if (!self.canvas || !self.printBounds) return;

    var bounds = self.printBounds;
    var objects = [];

    function guideProps(extra) {
      return Object.assign({
        selectable: false,
        evented: false,
        objectCaching: false,
        excludeFromExport: true,
        blvPrintGuide: true
      }, extra || {});
    }

    var step = Math.max(
      8,
      self.unitsPerInch * ((self.scaleX + self.scaleY) / 2)
    );
    var gridStroke = 'rgba(126, 168, 220, 0.9)';
    var x;
    var y;

    for (x = bounds.left + step; x < bounds.left + bounds.width - 1; x += step) {
      objects.push(new global.fabric.Line(
        [x, bounds.top, x, bounds.top + bounds.height],
        guideProps({ stroke: gridStroke, strokeWidth: 1 })
      ));
    }

    for (y = bounds.top + step; y < bounds.top + bounds.height - 1; y += step) {
      objects.push(new global.fabric.Line(
        [bounds.left, y, bounds.left + bounds.width, y],
        guideProps({ stroke: gridStroke, strokeWidth: 1 })
      ));
    }

    var label = self.printAreaLabel();
    if (label) {
      objects.push(new global.fabric.Text(label, guideProps({
        left: bounds.left + 8,
        top: bounds.top + 6,
        fontSize: Math.max(11, Math.min(14, bounds.width * 0.055)),
        fontFamily: 'Arial, Helvetica, sans-serif',
        fill: '#6ea4dc'
      })));
    }

    function rect(stroke, strokeWidth) {
      return new global.fabric.Rect(guideProps({
        left: bounds.left,
        top: bounds.top,
        width: bounds.width,
        height: bounds.height,
        fill: 'rgba(255,255,255,0)',
        stroke: stroke,
        strokeWidth: strokeWidth,
        strokeDashArray: [7, 5]
      }));
    }

    objects.push(rect('rgba(255,255,255,0.96)', 3.2));
    objects.push(rect('#2456c9', 1.25));

    self.printGuideObjects = objects;
    objects.forEach(function (obj) {
      self.canvas.add(obj);
    });
  };

  BlvnksArtworkEditor.prototype.loadArtwork = function () {
    var self = this;

    if (
      !self.canvas ||
      !self.options.artworkUrl
    ) {
      if (self.canvas) self.canvas.requestRenderAll();
      return;
    }

    var loadId =
      (self._artworkLoadId || 0) + 1;

    self._artworkLoadId = loadId;

    var artworkUrl =
      self.options.artworkUrl;

    loadFabricImage(
      artworkUrl,
      function (art) {
        if (
          self.destroyed ||
          !self.canvas ||
          loadId !== self._artworkLoadId
        ) {
          return;
        }

        if (!art) {
          self.emitError(
            new Error('Could not load artwork into editor')
          );
          return;
        }

        self.artworkObject = art;
        self.artworkAspect =
          (art.width && art.height)
            ? art.width / art.height
            : null;

        var targetWidth =
          self.state.widthIn *
          self.unitsPerInch *
          self.scaleX;

        var scale =
          targetWidth /
          Math.max(art.width || 1, 1);

        art.set({
          left: self.state.cx * self.scaleX,
          top: self.state.cy * self.scaleY,
          originX: 'center',
          originY: 'center',
          angle: self.state.angle,
          scaleX: scale,
          scaleY: scale,
          transparentCorners: false,
          cornerColor: '#ffffff',
          cornerStrokeColor: '#111111',
          borderColor: '#111111',
          cornerStyle: 'circle',
          cornerSize: 10,
          borderScaleFactor: 1.2,
          rotatingPointOffset: 24,
          centeredRotation: true,
          objectCaching: false
        });

        art.setControlsVisibility({
          mt: false,
          mb: false,
          ml: false,
          mr: false
        });

        self.bindArtworkEvents(art);
        self.constrainArtwork();

        self.canvas.add(art);
        self.canvas.setActiveObject(art);
        self.canvas.bringToFront(art);
        self.syncStateFromArtwork();
        self.canvas.requestRenderAll();

        self.emitReady();
      }
    );
  };


  /* =========================================
     PUBLIC ARTWORK CONTROLS
  ========================================= */

  BlvnksArtworkEditor.prototype.clearArtwork = function () {
    var self = this;

    /*
     * Invalidate an artwork request that may still
     * be loading asynchronously.
     */
    self._artworkLoadId =
      (self._artworkLoadId || 0) + 1;

    if (
      self.canvas &&
      self.artworkObject
    ) {
      try {
        self.canvas.remove(
          self.artworkObject
        );
      } catch (err) {
        console.warn(
          '[blvnks-editor] artwork removal failed',
          err
        );
      }
    }

    self.artworkObject = null;
    self.artworkAspect = null;

    self.options.artworkUrl = null;
    self.options.artworkName = '';

    if (self.canvas) {
      self.canvas.discardActiveObject();
      self.canvas.requestRenderAll();
    }
  };


  BlvnksArtworkEditor.prototype.setArtwork = function (
    url,
    name,
    options
  ) {
    var self = this;

    options = options || {};

    self.clearArtwork();

    if (!url) {
      return;
    }

    self.options.artworkUrl = url;
    self.options.artworkName =
      name || '';

    if (options.widthIn != null) {
      self.state.widthIn = clamp(
        finiteNumber(
          options.widthIn,
          self.state.widthIn
        ),
        self.minWidthIn,
        self.maxWidthIn
      );
    }

    if (options.cx != null) {
      self.state.cx =
        finiteNumber(
          options.cx,
          self.state.cx
        );
    }

    if (options.cy != null) {
      self.state.cy =
        finiteNumber(
          options.cy,
          self.state.cy
        );
    }

    if (options.angle != null) {
      self.state.angle = clamp(
        normalizeSignedAngle(
          options.angle
        ),
        self.minAngle,
        self.maxAngle
      );
    }

    self.loadArtwork();
  };


  BlvnksArtworkEditor.prototype.bindArtworkEvents = function (art) {
    var self = this;

    art.on('moving', function () {
      self.constrainArtwork();
      self.syncStateFromArtwork();
      self.emitTransform('move');
    });

    art.on('scaling', function () {
      self.constrainArtwork();
      self.syncStateFromArtwork();
      self.emitTransform('scale');
    });

    art.on('rotating', function () {
      var angle = normalizeSignedAngle(art.angle);

      angle = clamp(
        angle,
        self.minAngle,
        self.maxAngle
      );

      art.set('angle', angle);

      self.constrainArtwork();
      self.syncStateFromArtwork();
      self.emitTransform('rotate');
    });

    art.on('modified', function (evt) {
      self.constrainArtwork();
      self.syncStateFromArtwork();

      var action =
        evt &&
        evt.transform &&
        evt.transform.action
          ? String(evt.transform.action)
          : 'modify';

      self.emitCommit(action);
    });
  };

  BlvnksArtworkEditor.prototype.constrainArtwork = function () {
    var obj = this.artworkObject;
    var bounds = this.printBounds;

    if (!obj || !bounds) return;

    var currentWidthIn = this.objectWidthIn();

    if (currentWidthIn > this.maxWidthIn) {
      var maxFactor =
        this.maxWidthIn /
        Math.max(currentWidthIn, 0.001);

      obj.scaleX *= maxFactor;
      obj.scaleY *= maxFactor;
    }

    currentWidthIn = this.objectWidthIn();

    if (currentWidthIn < this.minWidthIn) {
      var minFactor =
        this.minWidthIn /
        Math.max(currentWidthIn, 0.001);

      obj.scaleX *= minFactor;
      obj.scaleY *= minFactor;
    }

    obj.setCoords();
    var box = obj.getBoundingRect(true, true);

    if (
      box.width > bounds.width ||
      box.height > bounds.height
    ) {
      var fitFactor = Math.min(
        bounds.width / Math.max(box.width, 1),
        bounds.height / Math.max(box.height, 1)
      ) * 0.995;

      if (
        isFinite(fitFactor) &&
        fitFactor > 0 &&
        fitFactor < 1
      ) {
        obj.scaleX *= fitFactor;
        obj.scaleY *= fitFactor;
        obj.setCoords();
        box = obj.getBoundingRect(true, true);
      }
    }

    var dx = 0;
    var dy = 0;

    if (box.left < bounds.left) {
      dx = bounds.left - box.left;
    } else if (
      box.left + box.width >
      bounds.left + bounds.width
    ) {
      dx =
        (bounds.left + bounds.width) -
        (box.left + box.width);
    }

    if (box.top < bounds.top) {
      dy = bounds.top - box.top;
    } else if (
      box.top + box.height >
      bounds.top + bounds.height
    ) {
      dy =
        (bounds.top + bounds.height) -
        (box.top + box.height);
    }

    obj.left += dx;
    obj.top += dy;
    obj.setCoords();
  };

  BlvnksArtworkEditor.prototype.objectWidthIn = function () {
    if (
      !this.artworkObject ||
      !this.scaleX ||
      !this.unitsPerInch
    ) {
      return this.state.widthIn;
    }

    return (
      this.artworkObject.getScaledWidth() /
      this.scaleX /
      this.unitsPerInch
    );
  };

  BlvnksArtworkEditor.prototype.syncStateFromArtwork = function () {
    var art = this.artworkObject;

    if (!art) return;

    this.state = {
      cx: round2(art.left / this.scaleX),
      cy: round2(art.top / this.scaleY),
      angle: round2(
        clamp(
          normalizeSignedAngle(art.angle),
          this.minAngle,
          this.maxAngle
        )
      ),
      widthIn: round2(
        clamp(
          this.objectWidthIn(),
          this.minWidthIn,
          this.maxWidthIn
        )
      )
    };
  };

  BlvnksArtworkEditor.prototype.getState = function () {
    var state = {
      cx: this.state.cx,
      cy: this.state.cy,
      angle: this.state.angle,
      widthIn: this.state.widthIn,
      heightIn: null
    };

    if (
      this.artworkAspect &&
      this.artworkAspect > 0
    ) {
      state.heightIn = round2(
        state.widthIn /
        this.artworkAspect
      );
    }

    return state;
  };

  BlvnksArtworkEditor.prototype.setWidthIn = function (widthIn) {
    var art = this.artworkObject;

    if (!art) return;

    widthIn = clamp(
      finiteNumber(widthIn, this.state.widthIn),
      this.minWidthIn,
      this.maxWidthIn
    );

    var targetWidth =
      widthIn *
      this.unitsPerInch *
      this.scaleX;

    var currentWidth =
      Math.max(art.getScaledWidth(), 0.001);

    var factor =
      targetWidth /
      currentWidth;

    art.scaleX *= factor;
    art.scaleY *= factor;

    this.constrainArtwork();
    this.syncStateFromArtwork();
    this.canvas.requestRenderAll();
    this.emitCommit('scale');
  };

  BlvnksArtworkEditor.prototype.setAngle = function (angle) {
    var art = this.artworkObject;

    if (!art) return;

    angle = clamp(
      normalizeSignedAngle(angle),
      this.minAngle,
      this.maxAngle
    );

    art.set('angle', angle);

    this.constrainArtwork();
    this.syncStateFromArtwork();
    this.canvas.requestRenderAll();
    this.emitCommit('rotate');
  };

  BlvnksArtworkEditor.prototype.setGarmentImage = function (url, onLoaded) {
    var self = this;

    function done(garment) {
      if (typeof onLoaded === 'function') onLoaded(garment || null);
    }

    if (!self.canvas || !url) {
      done(null);
      return;
    }

    var nextUrl = String(url);
    var current = String(
      self.garmentUrl ||
      (self.options && self.options.garmentUrl) ||
      ''
    );

    if (current === nextUrl) {
      done(self.garmentObject || null);
      return;
    }

    var loadId = (self._garmentLoadId || 0) + 1;
    self._garmentLoadId = loadId;

    loadFabricImage(nextUrl, function (garment) {
      if (
        self.destroyed ||
        !self.canvas ||
        self._garmentLoadId !== loadId
      ) {
        return;
      }

      if (!garment) {
        done(null);
        return;
      }

      var width = self.canvas.getWidth();
      var height = self.canvas.getHeight();
      var garmentScale = Math.min(
        width / Math.max(garment.width || 1, 1),
        height / Math.max(garment.height || 1, 1)
      );

      garment.set({
        left: width / 2,
        top: height / 2,
        originX: 'center',
        originY: 'center',
        scaleX: garmentScale,
        scaleY: garmentScale,
        selectable: false,
        evented: false,
        objectCaching: false,
        excludeFromExport: true,
        blvGarmentObject: true
      });

      if (self.garmentObject) {
        try {
          self.canvas.remove(self.garmentObject);
        } catch (err) {
          console.warn('[blvnks-editor] garment replacement failed', err);
        }
      }

      self.garmentObject = garment;
      self.garmentUrl = nextUrl;

      if (self.options) {
        self.options.garmentUrl = nextUrl;
      }

      self.canvas.add(garment);
      garment.sendToBack();
      self.canvas.requestRenderAll();
      done(garment);
    });
  };

  BlvnksArtworkEditor.prototype.refreshPrintGuide = function () {
    var self = this;

    if (!self.canvas) return;

    (self.printGuideObjects || []).forEach(function (obj) {
      try {
        self.canvas.remove(obj);
      } catch (err) {
        /* Guide may already have been removed. */
      }
    });

    self.printGuideObjects = [];
    self.addPrintGuide();

    var above = self.canvas.getObjects().filter(function (obj) {
      return !obj.blvPrintGuide && !obj.blvGarmentObject;
    });

    if (self.garmentObject) {
      self.garmentObject.sendToBack();
    }

    above.forEach(function (obj) {
      self.canvas.bringToFront(obj);
    });

    self.canvas.requestRenderAll();
  };

  BlvnksArtworkEditor.prototype.setPrintGeometry = function (printArea, unitsPerInch) {
    if (unitsPerInch != null) {
      this.unitsPerInch = Math.max(
        0.0001,
        finiteNumber(unitsPerInch, this.unitsPerInch)
      );
      this.options.unitsPerInch = this.unitsPerInch;
    }

    if (printArea) {
      this.options.printArea = printArea;
      this.printBounds = this.buildPrintBounds(printArea);
    }

    this.refreshPrintGuide();
  };

  BlvnksArtworkEditor.prototype.objectSizeInches = function (obj) {
    obj = obj || this.artworkObject;

    if (
      !obj ||
      !this.scaleX ||
      !this.scaleY ||
      !this.unitsPerInch
    ) {
      return { width: 0, height: 0 };
    }

    return {
      width: obj.getScaledWidth() / this.scaleX / this.unitsPerInch,
      height: obj.getScaledHeight() / this.scaleY / this.unitsPerInch
    };
  };

  BlvnksArtworkEditor.prototype.emitReady = function () {
    if (typeof this.options.onReady === 'function') {
      this.options.onReady(
        this.getState(),
        this
      );
    }
  };

  BlvnksArtworkEditor.prototype.emitTransform = function (action) {
    if (typeof this.options.onTransform === 'function') {
      this.options.onTransform(
        this.getState(),
        { action: action },
        this
      );
    }
  };

  BlvnksArtworkEditor.prototype.emitCommit = function (action) {
    if (typeof this.options.onCommit === 'function') {
      this.options.onCommit(
        this.getState(),
        { action: action },
        this
      );
    }
  };

  BlvnksArtworkEditor.prototype.emitError = function (err) {
    if (typeof this.options.onError === 'function') {
      this.options.onError(err, this);
      return;
    }

    console.warn('[blvnks-editor]', err);
  };

  BlvnksArtworkEditor.prototype.destroy = function () {
    this.destroyed = true;

    try {
      if (this.canvas) {
        this.canvas.dispose();
      }
    } catch (err) {
      console.warn(
        '[blvnks-editor] dispose failed',
        err
      );
    }

    this.canvas = null;
    this.artworkObject = null;
    this.garmentObject = null;
    this.printGuideObjects = [];
  };

  BlvnksArtworkEditor.normalizeSignedAngle =
    normalizeSignedAngle;

  BlvnksArtworkEditor.normalizeSvgPreview =
    normalizeSvgPreview;

  global.BlvnksArtworkEditor =
    BlvnksArtworkEditor;

})(window);
